import React from 'react';
import PropTypes from 'prop-types';
import { Cushion, Grid, Hide, Rectangle, Shadow, Space, Typography } from '@pitchero/react-ui';
import { find } from 'lodash';
import Icon from '../icon';
import { Trans } from '../../lib/i18n';
import ScorecardBatsman from './scorecard-batsman';

const ScorecardBatting = ({
  batsmen,
  extrasTotal,
  extrasSummary,
  gameType,
  runs,
  summary,
  team,
}) => {
  const isPairs = gameType === 'pairs';
  const hasBallsFacedStats = find(batsmen, (batter) => batter.ballsFaced !== null) !== null;

  let statColumnCount = 3;
  let palmStatColumnCount = 2;
  if (hasBallsFacedStats) {
    statColumnCount += isPairs ? 1 : 2;
    palmStatColumnCount += isPairs ? 0 : 1;
  }

  if (isPairs) palmStatColumnCount += 2;
  if (isPairs) statColumnCount += 2;
  const gridColumns = `minmax(100px, 55%) repeat(${statColumnCount}, 1fr)`;
  const gridResponsive = [
    {
      maxWidth: 'palm',
      props: { columns: `minmax(100px, 50%) repeat(${palmStatColumnCount}, 1fr)` },
    },
  ];
  return (
    <Space bottom="large">
      <Shadow>
        <Rectangle radius="roundedTop" fill="alabaster">
          <Cushion
            all="small"
            left="medium"
            responsive={[{ maxWidth: 'tab', props: { left: 'small' } }]}
            component="div"
          >
            <Grid columns={gridColumns} responsive={gridResponsive} alignItems="center">
              <Typography component="div" color="black" preset="tag--large">
                <Space right="xsmall">
                  <Icon name="cricket-bat" size={18} topOffset={4} />
                </Space>
                <Trans i18nKey="teams:batting_title">Batting</Trans>
              </Typography>
              <Typography component="div" color="dustygrey" preset="description" align="center">
                <Trans i18nKey="teams:runs_title">Runs</Trans>
              </Typography>
              {hasBallsFacedStats && (
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:balls_title">Balls</Trans>
                </Typography>
              )}
              <Hide maxWidth={hasBallsFacedStats ? 'palm' : 0}>
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:fours_title">4s</Trans>
                </Typography>
              </Hide>
              <Hide maxWidth={hasBallsFacedStats ? 'palm' : 0}>
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:sixes_title">6s</Trans>
                </Typography>
              </Hide>
              {hasBallsFacedStats && !isPairs && (
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:strike_rate_title">SR</Trans>
                </Typography>
              )}
              {isPairs && (
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:times_out_title">Times Out</Trans>
                </Typography>
              )}
              {isPairs && (
                <Typography component="div" color="dustygrey" preset="description" align="center">
                  <Trans i18nKey="teams:net_Score_title">Net score</Trans>
                </Typography>
              )}
            </Grid>
          </Cushion>
        </Rectangle>
        {batsmen.map((batsman) => (
          <ScorecardBatsman
            key={batsman.id}
            hasBallsFacedStats={hasBallsFacedStats}
            isPairs={isPairs}
            gridColumns={gridColumns}
            gridResponsive={gridResponsive}
            teamId={team.id}
            {...batsman}
          />
        ))}
        <Rectangle fill="alabaster">
          <Cushion
            vertical="medium"
            right="small"
            left="medium"
            responsive={[{ maxWidth: 'tab', props: { left: 'small' } }]}
            component="div"
          >
            <Grid columns={gridColumns} responsive={gridResponsive} alignItems="center">
              <div>
                <Typography component="div" color="black" preset="tag--large">
                  <Trans i18nKey="teams:extras_title">Extras</Trans>
                </Typography>
                {extrasSummary && (
                  <Typography color="dustygrey" preset="description">
                    {`(${extrasSummary})`}
                  </Typography>
                )}
              </div>
              <Typography component="div" color="black" preset="subtitle--large" align="center">
                {extrasTotal}
              </Typography>
            </Grid>
          </Cushion>
        </Rectangle>
        <Rectangle fill={team.colours.primary} radius="roundedBottom">
          <Cushion
            vertical="medium"
            right="small"
            left="medium"
            responsive={[{ maxWidth: 'tab', props: { left: 'small' } }]}
            component="div"
          >
            <Grid columns={gridColumns} responsive={gridResponsive} alignItems="center">
              <div>
                <Typography
                  component="div"
                  color={team.colours.primary_contrast}
                  preset="tag--large"
                >
                  <Trans i18nKey="teams:batting_total_title">Total</Trans>
                </Typography>
                {summary && (
                  <Typography color={team.colours.primary_contrast} preset="description">
                    {`(${summary})`}
                  </Typography>
                )}
              </div>
              <Typography
                component="div"
                color={team.colours.primary_contrast}
                preset="subtitle--large"
                align="center"
              >
                {runs}
              </Typography>
            </Grid>
          </Cushion>
        </Rectangle>
      </Shadow>
    </Space>
  );
};

ScorecardBatting.defaultProps = {
  extrasTotal: null,
  extrasSummary: null,
  runs: null,
  summary: null,
};

ScorecardBatting.propTypes = {
  batsmen: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  extrasTotal: PropTypes.number,
  extrasSummary: PropTypes.string,
  gameType: PropTypes.oneOf(['standard', 'pairs']).isRequired,
  runs: PropTypes.number,
  summary: PropTypes.string,
  team: PropTypes.shape({
    id: PropTypes.number,
    colours: PropTypes.shape({
      primary: PropTypes.string,
      primary_contrast: PropTypes.string,
      secondary: PropTypes.string,
    }),
    name: PropTypes.string,
  }).isRequired,
};

export default ScorecardBatting;
